import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  listUsers,
  updateUserStatus,
  WholesaleUser,
  UserStats,
} from '../../services/adminApi';

type FilterStatus = 'all' | 'pending' | 'approved' | 'rejected';
type SortField = 'name' | 'company' | 'status' | 'date';
type SortDirection = 'asc' | 'desc';

/**
 * Admin Users Page
 *
 * Full user management with filtering and actions
 */
export default function AdminUsers() {
  const [users, setUsers] = useState<WholesaleUser[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listUsers();
      setUsers(response.users);
      setStats(response.stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAction = async (
    userId: string,
    action: 'approve' | 'reject' | 'disable' | 'enable'
  ) => {
    try {
      setActionLoading(userId);
      await updateUserStatus(userId, action);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  // Filter users
  const filteredUsers = users.filter((user) => {
    // Status filter
    const statusMatch =
      filter === 'all' ||
      (filter === 'pending' &&
        (user.approvalStatus === 'pending' || !user.approvalStatus)) ||
      user.approvalStatus === filter;

    // Search filter
    const searchMatch =
      !searchTerm ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

    return statusMatch && searchMatch;
  }).sort((a, b) => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'name':
        return dir * a.name.localeCompare(b.name);
      case 'company':
        return dir * (a.companyName || '').localeCompare(b.companyName || '');
      case 'status':
        return dir * (a.approvalStatus || 'pending').localeCompare(b.approvalStatus || 'pending');
      case 'date':
        return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-workshop-elevated border-t-accent rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-400 mb-4">{error}</div>
        <button onClick={fetchData} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Status Filter */}
        <div className="flex gap-2">
          {(['all', 'pending', 'approved', 'rejected'] as FilterStatus[]).map(
            (status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wide rounded transition-colors ${
                  filter === status
                    ? 'bg-leather text-cream'
                    : 'bg-white/5 text-cream hover:bg-white/10'
                }`}
              >
                {status}
                {status !== 'all' && stats && (
                  <span className="ml-1 opacity-70">
                    ({status === 'pending' ? stats.pending : stats[status]})
                  </span>
                )}
              </button>
            )
          )}
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-white/10 rounded-lg bg-workshop text-text focus:outline-none focus:ring-2 focus:ring-accent/50 text-sm"
          />
          <svg
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 text-text-muted">
            <p>No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06] text-left bg-white/5">
                  <SortableHeader field="name" label="User" current={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeader field="company" label="Company" current={sortField} direction={sortDirection} onSort={toggleSort} />
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-text-muted font-medium">
                    Tax ID
                  </th>
                  <SortableHeader field="status" label="Status" current={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeader field="date" label="Registered" current={sortField} direction={sortDirection} onSort={toggleSort} />
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-text-muted font-medium text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <motion.tr
                    key={user.userId}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-white/[0.04] last:border-0 hover:bg-white/5"
                  >
                    <td className="px-4 py-4">
                      <div className="font-medium text-cream">{user.name}</div>
                      <div className="text-sm text-text-muted">{user.email}</div>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {user.companyName || (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm font-mono">
                      {user.taxId || <span className="text-text-muted">—</span>}
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge
                        status={user.approvalStatus || 'pending'}
                        enabled={user.enabled}
                      />
                    </td>
                    <td className="px-4 py-4 text-sm text-text-muted">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-4 py-4">
                      <UserActions
                        user={user}
                        loading={actionLoading === user.userId}
                        onAction={(action) => handleAction(user.userId, action)}
                      />
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="text-sm text-text-muted text-center">
        Showing {filteredUsers.length} of {users.length} users
      </div>
    </div>
  );
}

/**
 * Sortable Table Header
 */
function SortableHeader({
  field,
  label,
  current,
  direction,
  onSort,
}: {
  field: SortField;
  label: string;
  current: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
}) {
  const isActive = current === field;
  return (
    <th
      onClick={() => onSort(field)}
      className="px-4 py-3 text-xs uppercase tracking-wide text-text-muted font-medium cursor-pointer hover:text-cream select-none"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive ? (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={direction === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        ) : (
          <svg className="w-3 h-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
          </svg>
        )}
      </span>
    </th>
  );
}

/**
 * Status Badge Component
 */
function StatusBadge({
  status,
  enabled,
}: {
  status: string;
  enabled: boolean;
}) {
  if (!enabled) {
    return (
      <span className="inline-flex items-center px-2 py-1 text-xs rounded bg-white/5 text-text-muted">
        Disabled
      </span>
    );
  }

  const colors: Record<string, string> = {
    pending: 'bg-amber-900/40 text-amber-300',
    approved: 'bg-green-900/40 text-green-300',
    rejected: 'bg-red-900/40 text-red-300',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 text-xs rounded capitalize ${
        colors[status] || colors.pending
      }`}
    >
      {status || 'pending'}
    </span>
  );
}

/**
 * User Actions Component
 */
function UserActions({
  user,
  loading,
  onAction,
}: {
  user: WholesaleUser;
  loading: boolean;
  onAction: (action: 'approve' | 'reject' | 'disable' | 'enable') => void;
}) {
  const [showMenu, setShowMenu] = useState(false);

  const status = user.approvalStatus || 'pending';
  const isPending = status === 'pending';
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';

  return (
    <div className="flex items-center justify-end gap-2">
      {/* Quick actions for pending users */}
      {isPending && (
        <>
          <button
            onClick={() => onAction('approve')}
            disabled={loading}
            className="px-3 py-1 text-xs uppercase tracking-wide bg-green-600 text-white rounded hover:bg-green-500 disabled:opacity-50 transition-colors"
          >
            {loading ? '...' : 'Approve'}
          </button>
          <button
            onClick={() => {
              if (confirm(`Reject ${user.name || user.email}? They will not be able to access the portal.`)) {
                onAction('reject');
              }
            }}
            disabled={loading}
            className="px-3 py-1 text-xs uppercase tracking-wide bg-red-600 text-white rounded hover:bg-red-500 disabled:opacity-50 transition-colors"
          >
            {loading ? '...' : 'Reject'}
          </button>
        </>
      )}

      {/* Actions menu for non-pending users */}
      {!isPending && (
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="px-3 py-1 text-xs uppercase tracking-wide border border-white/10 rounded hover:bg-white/5 transition-colors"
          >
            Actions
          </button>

          {showMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowMenu(false)}
              />
              <div className="absolute right-0 top-full mt-1 bg-workshop-overlay border border-white/10 rounded shadow-lg z-20 min-w-[120px]">
                {isApproved && (
                  <button
                    onClick={() => {
                      if (confirm(`Revoke access for ${user.name || user.email}? They will lose portal access.`)) {
                        onAction('reject');
                        setShowMenu(false);
                      }
                    }}
                    disabled={loading}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-red-900/20 text-red-600"
                  >
                    Revoke Access
                  </button>
                )}
                {isRejected && (
                  <button
                    onClick={() => {
                      onAction('approve');
                      setShowMenu(false);
                    }}
                    disabled={loading}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-green-900/20 text-green-600"
                  >
                    Approve
                  </button>
                )}
                {user.enabled ? (
                  <button
                    onClick={() => {
                      if (confirm(`Disable account for ${user.name || user.email}? They will not be able to sign in.`)) {
                        onAction('disable');
                        setShowMenu(false);
                      }
                    }}
                    disabled={loading}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-white/5 text-gray-600"
                  >
                    Disable Account
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      onAction('enable');
                      setShowMenu(false);
                    }}
                    disabled={loading}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-green-900/20 text-green-600"
                  >
                    Enable Account
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Format date string
 */
function formatDate(dateString: string): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
